import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar, SidebarContent } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette, useCommandPalette } from './CommandPalette';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { PageSkeleton } from '@/components/feedback/states';
import { RouteErrorBoundary } from '@/app/RouteErrorBoundary';
import { WelcomeTour } from '@/features/onboarding/WelcomeTour';
import { ScreenTour } from '@/features/onboarding/ScreenTour';
import { useUiStore } from '@/stores/ui.store';

/**
 * The authenticated frame: sidebar + topbar + routed content.
 *
 * The `Suspense` boundary is what makes the lazy route groups in `router.jsx` work — a
 * chunk still downloading renders a skeleton in the content area while the chrome stays
 * put, rather than blanking the whole page.
 */
export const AppShell = () => {
  const { open, setOpen } = useCommandPalette();
  const sidebarOpen = useUiStore((state) => state.sidebarOpen);
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <Sidebar />

      {/* Below `lg` the same nav content becomes an off-canvas drawer. */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="start" className="w-72 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent onNavigate={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenSearch={() => setOpen(true)} />

        <main className="flex-1 overflow-y-auto scrollbar-thin">
          <RouteErrorBoundary>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>

      <CommandPalette open={open} onOpenChange={setOpen} />

      {/*
       * Both live at the shell, not on individual pages. The welcome dialog has to be
       * able to open over *whatever* the user landed on, and `ScreenTour` reads the route
       * itself — putting either on a page would mean remembering to add it to every new
       * screen, which is the kind of thing that gets remembered for four screens.
       */}
      <WelcomeTour />
      <ScreenTour />
    </div>
  );
};

export default AppShell;
